// Pure helpers for Google One Tap (no path aliases, so node --test can import them).

export interface GoogleOneTapSettings {
  enable?: boolean;
  client_id?: string;
  auto_show?: boolean;
  prompt_delay?: number | string;
  allowed_domains?: string[] | string;
  fallback_button?: boolean;
  cancel_on_tap_outside?: boolean;
  context?: 'signin' | 'signup' | 'use';
  // Added by the server: effective client ID (override or Google login client ID).
  resolved_client_id?: string;
  has_login_client_id?: boolean;
}

export const ONE_TAP_DEFAULTS = {
  enable: false,
  client_id: '',
  auto_show: true,
  prompt_delay: 2,
  allowed_domains: [] as string[],
  fallback_button: true,
  cancel_on_tap_outside: true,
  context: 'signin' as const,
};

/** Public and auth pages only: never inside Drive, admin or account settings. */
export const ONE_TAP_PATHS = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/pricing',
  '/contact',
  '/pages/',
];

export function normalizeDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .replace(/\.$/, '');
}

export function normalizeDomains(value: GoogleOneTapSettings['allowed_domains']): string[] {
  let list: unknown = value ?? [];
  if (typeof list === 'string') {
    try {
      const parsed = JSON.parse(list);
      list = Array.isArray(parsed) ? parsed : list.split(',');
    } catch {
      list = (list as string).split(',');
    }
  }
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  for (const item of list) {
    const d = normalizeDomain(String(item ?? ''));
    if (d && !out.includes(d)) out.push(d);
  }
  return out;
}

export function hostAllowed(host: string, domains: string[]): boolean {
  if (!domains.length) return true;
  const h = host.toLowerCase().split(':')[0];
  return domains.some(d => h === d || h.endsWith(`.${d}`));
}

export function isOneTapPath(pathname: string, basePath = ''): boolean {
  let path = pathname;
  const base = basePath.replace(/\/$/, '');
  if (base && path.startsWith(base)) path = path.slice(base.length) || '/';
  path = path.replace(/\/+$/, '') || '/';
  return ONE_TAP_PATHS.some(p =>
    p.endsWith('/') && p !== '/' ? path.startsWith(p) : path === p,
  );
}

export function promptDelayMs(value: GoogleOneTapSettings['prompt_delay']): number {
  const n = Number(value ?? ONE_TAP_DEFAULTS.prompt_delay);
  if (!Number.isFinite(n) || n < 0) return ONE_TAP_DEFAULTS.prompt_delay * 1000;
  return Math.min(n, 60) * 1000;
}

export function shouldLoadOneTap(input: {
  settings?: GoogleOneTapSettings | null;
  isLoggedIn: boolean;
  host: string;
  pathname: string;
  basePath?: string;
  isPreview?: boolean;
}): boolean {
  const s = input.settings;
  if (!s?.enable || !s.resolved_client_id) return false;
  if (input.isLoggedIn || input.isPreview) return false;
  if (!hostAllowed(input.host, normalizeDomains(s.allowed_domains))) return false;
  return isOneTapPath(input.pathname, input.basePath);
}
